import { User } from 'oidc-client-ts';
import { createApiClient } from '../oidc-api';
import {
  CompletionRequestDto,
  CompletionResponseDto,
  PaginatedResponseUserChatMessageDto
} from './types';

// Helper for retry mechanism
const withRetry = async <T>(
  promiseFn: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000
): Promise<T> => {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await promiseFn();
    } catch (error) {
      lastError = error;
      // Wait before retrying
      if (attempt < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  throw lastError;
};

/**
 * Create a completion (send a message to AI assistant) using the profile API
 * We don't use retry for completions as they are state-changing operations
 * and we don't want to risk duplicate message submissions
 */
export const createCompletion = async (
  user: User | null,
  requestData: CompletionRequestDto
): Promise<CompletionResponseDto[]> => {
  const api = createApiClient(user);

  // Use the new profile chats endpoint from swagger
  // The x-user-id is already handled by the API Gateway
  return api.post<CompletionResponseDto[]>('/v1/profile/chats', requestData);
};

/**
 * Get recent completions history for the current user using the profile API
 * Using retry for reliability
 */
export const getCompletionsHistory = async (
  user: User | null,
  pageNumber: number = 1,
  pageSize: number = 10
): Promise<PaginatedResponseUserChatMessageDto> => {
  const api = createApiClient(user);

  // Use the new profile chats history endpoint from swagger
  // The x-user-id is already handled by the API Gateway
  const endpoint = `/v1/profile/chats/history?page_number=${pageNumber}&page_size=${pageSize}`;
  return withRetry(() => api.get<PaginatedResponseUserChatMessageDto>(endpoint));
};

/**
 * Get a specific completion by ID
 * Using retry for reliability when fetching a specific completion
 *
 * Note: This endpoint may need updating to match the new API structure when needed
 */
export const getCompletionById = async (
  user: User | null,
  completionId: string
): Promise<CompletionResponseDto> => {
  const api = createApiClient(user);
  return withRetry(() => api.get<CompletionResponseDto>(`/v1/completions/${completionId}`));
};